import { type RefObject, useEffect, useState } from 'react';

export type UseInViewportOptions = {
  /** Preload before the element enters the viewport. */
  rootMargin?: string;
  /** Stay visible after the first intersection (default true). */
  once?: boolean;
};

const isNearViewport = (element: Element, rootMargin: string): boolean => {
  const rect = element.getBoundingClientRect();
  const margin = Number.parseFloat(rootMargin) || 0;
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight;
  return rect.top < viewportHeight + margin && rect.bottom > -margin;
};

/** True when `ref` intersects the viewport (optionally with preload margin). */
export const useInViewport = (
  ref: RefObject<Element | null>,
  { rootMargin = '160px', once = true }: UseInViewportOptions = {},
): boolean => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    if (isNearViewport(element, rootMargin)) {
      setVisible(true);
      if (once) {
        return;
      }
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true);
          if (once) {
            observer.disconnect();
          }
        } else if (!once) {
          setVisible(false);
        }
      },
      { rootMargin },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [once, ref, rootMargin]);

  return visible;
};
